import {React, _} from '@api'
import {HexPdf, PdfProductCardRendererProps, usePdfStore} from '@modules/pdf'
import {PDFComponents} from '@modules/pdf/templates/vlp/VlpProductCard.styles'
import {ICatalogFieldConfig} from '@stores'
import {FunctionComponent} from 'react'
import {RegionSelection, SectionalPDFFieldRenderer} from './index'
import ReactPDF, {View, Text, Image, Link} from '@react-pdf/renderer'
import {VLPProductCardStore} from './VLPProductCardStore'
import {NoImageRH, RedStar, useStores} from '@design-board'

const foreignLanguageRegex = /[\u3000-\u303f\u3040-\u309f\u30a0-\u30ff\uff00-\uff9f\u4e00-\u9faf\u3400-\u9fbf]/

const textToDataURL = _.memoize((s: string, font: string, fontSize: number) => {
  //s = s.length > 15 ? `${s.substring(0, 15)}...` : s

  font = font ?? `${fontSize * 4}px VIP`

  const canvas = document.createElement('canvas'),
    context = canvas.getContext('2d')

  context.font = font
  const measuredText = context.measureText(s)
  // @ts-ignore
  canvas.height = measuredText.fontBoundingBoxAscent
  canvas.width = measuredText.width
  canvas.style.letterSpacing = '.1px'
  // It switches back to default after calling measureText
  context.font = font
  context.textBaseline = 'middle'
  context.fillText(s, 0, canvas.height / 2)
  const dataUrl = canvas.toDataURL('image/png', 1)
  // console.log({s, dataUrl, measuredText, font: context.font, width: canvas.width, height: canvas.height})
  canvas.remove()
  return {dataUrl, width: canvas.width, height: canvas.height}
})

export const LangAwareText: React.FC<ReactPDF.TextProps & { font?: string, fontSize?: number, textRenderer?: FunctionComponent<ReactPDF.TextProps> }> = ({font, fontSize, textRenderer, children, ...props}) => {
  const pdf = usePdfStore(),
    {defaultConfigForTemplate: {productPage: {fontFamily}}, stores} = pdf

  if (_.isString(children)) {
    const s = children
    const {LineItemText} = PDFComponents(stores)

    if (s.match(foreignLanguageRegex)) {
      return <LineItemText {...props} children={children} style={{fontFamily: fontFamily.regionalText}}/>
    }
  }

  return textRenderer ? React.createElement(textRenderer, {...props, children}) : <Text {...props} children={children}/>
}

export const VlpProductCardLineItem: React.FunctionComponent<{ style?, left?, right?, leftTextStyle?, rightTextStyle?, columns?, showLabel? }> = ({style, left, right, leftTextStyle, rightTextStyle, columns, showLabel = true}) => {
  const {strings, stores} = usePdfStore(),
    {LineItem, LineItemLeft, LineItemRight, LineItemText} = PDFComponents(stores)

  //Todo - support nested cards (styles/colorways) in PDF
  if (_.isArray(right)) {
    return null
  }
  return (
    <LineItem columns={columns} style={style}>
      {left === null || !showLabel ? null : <LineItemLeft>{_.isNumber(left) || _.isString(left) ? <LangAwareText textRenderer={LineItemText} style={leftTextStyle}>{left.toString()}</LangAwareText> : left}</LineItemLeft>}
      {right === null ? null : <LineItemRight>
        {_.isNumber(right) || _.isString(right) || _.isBoolean(right)
         ? <LangAwareText textRenderer={LineItemText} style={rightTextStyle}>{right === '' || _.isUndefined(right) ? strings.noValue : right.toString()}</LangAwareText>
         : right ?? strings.noValue}
      </LineItemRight>}
    </LineItem>
  )
}

const NoImagePlaceholderPDF: React.FunctionComponent<{ placeholderImg?: string }> = React.memo((props) => {
  const {catalog, stores} = usePdfStore(),
    {NoImage, NoImageText} = PDFComponents(stores)
  const placeholderImg = props.placeholderImg || catalog.placeholderImg
  if (placeholderImg) {
    if (!placeholderImg.startsWith('data:image/svg+xml;')) {
      return <NoImage src={placeholderImg}/>
    }
    console.debug(`Skipping image placeholder in PDF due to unsupported data:image/svg type`)
  }

  return <NoImageText>No Image</NoImageText>
})

const specifications: Array<{ name: string, cardViewName: string }> = [
  {name: 'materials', cardViewName: 'material'},
  {name: 'colors', cardViewName: 'color'},
  {name: 'styles', cardViewName: 'style'},
  {name: 'hardware', cardViewName: 'hardware'}
]

const OBJECT_TYPE = '[object Object]'

const VlpProductItem: React.FunctionComponent<Partial<PdfProductCardRendererProps>> = ({data, store}) => {
  const stores = useStores(),
    {
      vlp, tags,
      catalog: {placeholderImgFor, sectionById, labelForFieldName_currentCatalog, fieldByName, settings: {showCardLabels}, imageSource, cv: {cardViewWithAttributes}, catalogConfig: {features}},
      es: {readField},
      ui: {settings: {numberOfPDFVisibleViewOptions}}
    } = stores,
    {visiblePdfCardFields, inactiveLineHeight, inactiveDegree, cardWidth, imageHeight, labelForFieldValue, config: {imageRatio, columns}, isComputedField, computedValue} = usePdfStore(),
    {RootView, Title, Specs, DetailView, VlpImageContainer, ProductImage, NoImageView, EmptyProductImage, StarImage, NoImageProduct} = PDFComponents(stores),
    {
      strings, imageUrl, isHex, isImage, imageWithBackground, alignSelf, backgroundImage, id, catalog
    } = store
  return (
    <RootView key={id} imageRatio={imageRatio} columns={columns}>
      <VlpImageContainer imageRatio={imageRatio} columns={columns}>
        {
          isHex ? <HexPdf hex={imageUrl} style={{}}/> :
          isImage ? imageWithBackground ? <NoImageView justifyContent={alignSelf}>
                    {backgroundImage && <NoImageProduct src={backgroundImage}/>}
                    {imageUrl && <ProductImage src={imageUrl ?? ''} width={0} height={0} style={{display: 'flex', margin: 8, height: 'auto', width: 'auto'}}/>}
                  </NoImageView> : imageUrl && <ProductImage src={imageUrl ?? ''} width={0} height={0}/> :
          <EmptyProductImage>
            {
              <NoImagePlaceholderPDF placeholderImg={placeholderImgFor(data)}/>
            }
          </EmptyProductImage>
        }
        {(features?.card?.icons ?? []).map(icon => _.get(data, icon.field) ? <StarImage key={id} src={RedStar} source={RedStar}/> : null).filter(_.identity)}

        {((data?.colorway && data.colorway.active === false) || (data?.attributes?.Active === false)) &&
		 <View style={{
           transform: (`rotate(${inactiveDegree}deg)`),
           backgroundColor: 'red',
           height: inactiveLineHeight,
           top: Math.floor((imageHeight - inactiveLineHeight) / 2),
           margin: 0,
           right: (cardWidth / 2),
           position: 'absolute',
           width: '2px'
         }}/>}
      </VlpImageContainer>
      {/*Transform rotate wasn't working with styled component. Hence added inline styles*/}

      <DetailView columns={columns}>
        {_.take(visiblePdfCardFields, numberOfPDFVisibleViewOptions).map(
          v => {
            const {field, group, section, def, renderer} = v,
              sectionConfig = section ? sectionById[section] : null
            let fieldDef: ICatalogFieldConfig = catalog._fieldByName[field],
              {val, valAsString} = readField(data, field, group?.field ?? (typeof fieldDef?.filter === 'boolean' ? '' : fieldDef?.filter?.subField))
            if (def?.type === 'Boolean' && val == null) {
              val = false
            }
            let attrValue = isComputedField(v) ? computedValue(data, v) : (group?.field || (typeof fieldDef?.filter === 'boolean' ? '' : fieldDef?.filter?.subField)) ? valAsString : val,
              right = labelForFieldValue(v, attrValue) ?? strings.emptyLabel
            if (def?.type === 'TagList' && attrValue) {
              const valAsObject = attrValue?.startsWith(OBJECT_TYPE)
              if (valAsObject) {
                right = ''
                right = val.map(v => {
                  return tags._byId[v.id]?.name ?? ''
                }).join(', ')
              }
            }
            if (def?.type === 'RefList' && !Array.isArray(attrValue) && attrValue && attrValue?.startsWith(OBJECT_TYPE)) {
              right = val.map(v => v.name === '' ? strings.noValue : v.name).filter(_.identity).join(', ')
            }
            if (fieldDef?.esIndex && attrValue && !Array.isArray(attrValue) && attrValue?.startsWith(OBJECT_TYPE)) {
              right = Array.isArray(val) ? val.map(v => v.name ?? '') : val.name ?? strings.noValue
            }
            if (renderer === 'percentage') {
              right = `${(Number(right) * 100).toFixed(2)}%`
            }
            return sectionConfig?.viewOptions ? <SectionalPDFFieldRenderer key={`${field}.${section}.${def?.type ?? 'String'}_${id}`} field={v} sectionConfig={sectionConfig} data={data}/>
                                              : // v.field == 'name' ? <LangAwareText textRenderer={Title} key={field} style={{maxLines: 1, textOverflow: 'ellipsis'}}>{readField(data, field).valAsString}</LangAwareText> : Intentionally comment this line - YFTS-8265 - Piyush Bhati
                   <VlpProductCardLineItem
                     columns={columns}
                     key={`${field}.${group?.field ?? ''}_${id}`}
                     left={`${labelForFieldName_currentCatalog(field)}: `}
                     showLabel={showCardLabels}
                     right={right}/>
          }
        )}
        <RegionSelection data={data}/>

        {vlp?.isVLPView && <Specs key={'specs'} specs={cardViewWithAttributes}>
          {cardViewWithAttributes.merch_product && data.merchProduct &&
		   <Title columns={columns} style={{marginLeft: 0}}>{data.merchProduct.name}</Title>
          }
		</Specs>}
        {
          !vlp?.isVLPView ? specifications.map(({name, cardViewName}) => {
            return cardViewWithAttributes[cardViewName] ?
                   <VlpProductCardLineItem
                     columns={columns}
                     key={`${name}_${id}`}
                     left={`${fieldByName(name).label ?? fieldByName[name]?.def?.displayName?.singular}: `}
                     showLabel={showCardLabels}
                     // leftTextStyle={{fontWeight: 'bold', fontFamily: 'Arial Bold'}}
                     right={data[name].title}
                   /> : null
          }) : null
        }
      </DetailView>
    </RootView>
  )
}

export const VlpProductCard: React.FunctionComponent<PdfProductCardRendererProps> = ({data, download}) => {
  const stores = useStores(),
    {catalog: {stores: catalogStores}} = stores,
    store = new VLPProductCardStore(catalogStores, data),
    {LineItemLink} = PDFComponents(stores)

  if (download) {
    return (
      <LineItemLink style={{textDecoration: 'none'}} src={store.itemUrl}>
        <VlpProductItem data={data} store={store}/>
      </LineItemLink>
    )
  }
  return (
    <>
      <VlpProductItem data={data} store={store}/>
    </>
  )
}




// WEBPACK FOOTER //
// ./src/modules/pdf/templates/vlp/VlpProductCard.tsx